import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '../../juristec-ui/core/Button';
import { downloadExcel } from './excel';

const Container = styled.div`
  color: ${({ theme }) => theme.color};

  .details-scroll {
    max-height: 60vh;
    overflow: auto;
    margin: 12px 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  th, td {
    padding: 10px 12px;
    border: 1px solid ${({ theme }) => theme.containerLine};
    text-align: left;
    white-space: nowrap;
  }

  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.container};
  }

  .details-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  [role='alert'] {
    color: ${({ theme }) => theme.error};
  }
`;

export default function ReportGridDetails({ payload, loadDetails }) {
    const [exporting, setExporting] = useState(false);
    const [exportedCount, setExportedCount] = useState(0);
    const [exportError, setExportError] = useState('');
    const exportRequest = useRef(null);

  const [offset, setOffset] = useState(0);
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const limit = 100;

    useEffect(() => () => {
    exportRequest.current?.abort();
    }, []);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError('');

    Promise.resolve()
      .then(() => loadDetails({
        ...payload,
        offset,
        limit,
      }, controller.signal))
      .then((data) => {
        if (!controller.signal.aborted) setResult(data);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) {
          setError(failure.message || 'Não foi possível carregar os detalhes.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [payload, loadDetails, offset, retry]);

    const exportAllDetails = async () => {
    if (exportRequest.current || !result?.total) return;

    const controller = new AbortController();
    exportRequest.current = controller;

    setExporting(true);
    setExportedCount(0);
    setExportError('');

    try {
        const pageSize = 200;
        let nextOffset = 0;
        let expectedTotal;
        let columns;
        const rows = [];

        while (true) {
        const page = await loadDetails({
            ...payload,
            offset: nextOffset,
            limit: pageSize,
        }, controller.signal);

        if (controller.signal.aborted) return;

        if (
            !Array.isArray(page.columns)
            || !Array.isArray(page.records)
            || !Number.isInteger(page.total)
            || page.total < 0
        ) {
            throw new Error('O backend retornou uma página inválida.');
        }

        if (!columns) {
            columns = page.columns;
            expectedTotal = page.total;

            if (expectedTotal === 0) {
            throw new Error('Nenhum registro disponível para exportar.');
            }

            // Uma linha é reservada ao cabeçalho.
            if (expectedTotal > 1048575) {
            throw new Error('O resultado excede o limite de linhas de uma planilha Excel.');
            }

            rows.push(columns);
        } else if (
            page.total !== expectedTotal
            || JSON.stringify(page.columns) !== JSON.stringify(columns)
        ) {
            throw new Error(
            'Os dados mudaram durante a exportação. Tente novamente.',
            );
        }

        if (
            page.records.length === 0
            && nextOffset < expectedTotal
        ) {
            throw new Error('A consulta terminou antes de retornar todos os registros.');
        }

        page.records.forEach((record) => {
            rows.push(columns.map((column) => record[column] ?? null));
        });

        nextOffset += page.records.length;
        setExportedCount(nextOffset);

        if (nextOffset >= expectedTotal) break;
        }

        if (!controller.signal.aborted) {
        downloadExcel(rows, 'detalhes-completos-do-relatorio');
        }
    } catch (failure) {
        if (!controller.signal.aborted) {
        setExportError(
            failure.message || 'Não foi possível exportar os registros.',
        );
        }
    } finally {
        if (exportRequest.current === controller) {
        exportRequest.current = null;
        setExporting(false);
        }
    }
    };

  return (
    <Container>
      {loading && <p role="status">Carregando detalhes…</p>}

      {error && (
        <>
          <p role="alert">{error}</p>
          <Button onClick={() => setRetry((value) => value + 1)}>
            Tentar novamente
          </Button>
        </>
      )}

      {!loading && !error && result && (
        <>
          <p>{result.total} registro(s) encontrado(s).</p>
          <Button
            type="button"
            variant="outlined"
            disabled={exporting || loading || !result?.total}
            onClick={exportAllDetails}
            >
            {exporting
                ? `Exportando… ${exportedCount} registros`
                : 'Exportar todos em Excel'}
            </Button>

            {exporting && (
            <Button
                type="button"
                variant="outlined"
                onClick={() => exportRequest.current?.abort()}
            >
                Cancelar exportação
            </Button>
            )}

            {exportError && <p role="alert">{exportError}</p>}

          {result.records.length === 0 ? (
            <p>Nenhum registro encontrado.</p>
          ) : (
            <div className="details-scroll">
              <table>
                <thead>
                  <tr>
                    {result.columns.map((column) => (
                      <th key={column} scope="col">{column}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {result.records.map((record, index) => (
                    <tr key={offset + index}>
                      {result.columns.map((column) => (
                        <td key={column}>
                          {record[column] == null
                            ? '—'
                            : String(record[column])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="details-actions">
            <Button
              variant="outlined"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Anterior
            </Button>

            <span>
              Página {Math.floor(offset / limit) + 1}
              {' de '}
              {Math.max(1, Math.ceil(result.total / limit))}
            </span>

            <Button
              variant="outlined"
              disabled={offset + limit >= result.total}
              onClick={() => setOffset(offset + limit)}
            >
              Próxima
            </Button>
          </div>
        </>
      )}
    </Container>
  );
}